import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuthorizationHeader, http } from '../config/http';
import Layouts from '../layouts/Layouts';
import { DinamicTitle } from '../utils/DinamicTitle';
import { toast } from 'react-toastify';
import { IconEye, IconEyeOff } from '@tabler/icons-react';

export default function ListUsers() {
  DinamicTitle('List Users || Komaladewi');
  const [users, setUsers] = useState([]);
  const [cabangList, setCabangList] = useState([]);
  const [filterRole, setFilterRole] = useState('Semua');
  const [filterStatus, setFilterStatus] = useState('Aktif');
  const [loading, setLoading] = useState(false);
  
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    username: '',
    email: '',
    password: '',
    password_confirmation: '',
    role: 'kasir',
    cabang_id: ''
  });

  const isUsernameDuplicate = useMemo(() => {
    if (!formData.username.trim() || !Array.isArray(users)) return false;
    return users.some(u => u.username?.toLowerCase() === formData.username.trim().toLowerCase());
  }, [formData.username, users]);

  const isEmailDuplicate = useMemo(() => {
    if (!formData.email.trim() || !Array.isArray(users)) return false;
    return users.some(u => u.email?.toLowerCase() === formData.email.trim().toLowerCase());
  }, [formData.email, users]);

  const isPasswordMismatch = useMemo(() => {
    if (!formData.password_confirmation) return false;
    return formData.password !== formData.password_confirmation;
  }, [formData.password, formData.password_confirmation]);

  const isGlobalRole = useMemo(() => {
    return formData.role === 'owner' || formData.role === 'admin' || formData.role === 'supervisor';
  }, [formData.role]);
  
  const navigate = useNavigate();

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await http.get('/users', {
        headers: { Authorization: getAuthorizationHeader() },
      });
      setUsers(res.data.data);
    } catch (err) {
      if (err.response && err.response.status === 401) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchCabang = async () => {
    try {
      const res = await http.get('/cabang', {
        headers: { Authorization: getAuthorizationHeader() },
      });
      setCabangList(res.data.data || res.data); // Adjust depending on response structure
    } catch (err) {
      console.error('Failed to fetch cabang', err);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchCabang();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const getRoleBadge = (roleProp) => {
    const roles = Array.isArray(roleProp) ? roleProp : (typeof roleProp === 'string' ? [roleProp] : []);
    const primaryRole = roles[0] || '';
    
    let colorClass = 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-300 border-slate-200 dark:border-slate-700';
    let label = primaryRole;

    switch (primaryRole) {
      case 'admin':
        colorClass = 'bg-purple-100 text-purple-800 dark:bg-purple-950/60 dark:text-purple-300 border-purple-200 dark:border-purple-800';
        label = 'Admin';
        break;
      case 'owner':
        colorClass = 'bg-indigo-100 text-indigo-800 dark:bg-indigo-950/60 dark:text-indigo-300 border-indigo-200 dark:border-indigo-800';
        label = 'Owner';
        break;
      case 'kasir':
        colorClass = 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800';
        label = 'Kasir';
        break;
      case 'kasir_pembelian':
        colorClass = 'bg-cyan-100 text-cyan-800 dark:bg-cyan-950/60 dark:text-cyan-300 border-cyan-200 dark:border-cyan-800';
        label = 'Kasir Pembelian';
        break;
      case 'verifikator':
        colorClass = 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300 border-amber-200 dark:border-amber-800';
        label = 'Verifikator';
        break;
      case 'operator':
        colorClass = 'bg-blue-100 text-blue-800 dark:bg-blue-950/60 dark:text-blue-300 border-blue-200 dark:border-blue-800';
        label = 'Operator';
        break;
      case 'operator_rumah':
        colorClass = 'bg-teal-100 text-teal-800 dark:bg-teal-950/60 dark:text-teal-300 border-teal-200 dark:border-teal-800';
        label = 'Operator Rumah';
        break;
      case 'kepala_toko':
        colorClass = 'bg-fuchsia-100 text-fuchsia-800 dark:bg-fuchsia-950/60 dark:text-fuchsia-300 border-fuchsia-200 dark:border-fuchsia-800';
        label = 'Kepala Toko';
        break;
      case 'supervisor':
        colorClass = 'bg-indigo-100 text-indigo-800 dark:bg-indigo-950/60 dark:text-indigo-300 border-indigo-200 dark:border-indigo-800';
        label = 'Supervisor';
        break;
      default:
        label = primaryRole ? primaryRole.replace(/_/g, ' ') : '-';
    }

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${colorClass}`}>
        {label}
      </span>
    );
  };

  const filteredUsers = users.filter(user => {
    const userRoles = Array.isArray(user.role) 
      ? user.role 
      : (typeof user.role === 'string' ? [user.role] : []);
    const matchRole = filterRole === 'Semua' || userRoles.includes(filterRole);
    const matchStatus = filterStatus === 'Semua' || (filterStatus === 'Aktif' ? user.is_active : !user.is_active);
    return matchRole && matchStatus;
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [filterRole, filterStatus]);

  const lastPage = Math.ceil(filteredUsers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedUsers = filteredUsers.slice(startIndex, startIndex + itemsPerPage);

  const toggleStatus = async (userId) => {
    try {
      setLoading(true);
      await http.patch(`/users/${userId}/status`, {}, {
        headers: { Authorization: getAuthorizationHeader() },
      });
      // Real-time reload data
      await fetchUsers();
    } catch (err) {
      console.error(err);
      setLoading(false); // only set to false on error, as fetchUsers handles it on success
    }
  };

  const handleForceLogout = async () => {
    if (!selectedUser) return;
    try {
      setLoading(true);
      const res = await http.post(`/users/${selectedUser.id}/force-logout`, {}, {
        headers: { Authorization: getAuthorizationHeader() },
      });
      toast.success(res.data?.message || 'Pengguna berhasil di-logout.');
      await fetchUsers();
      setIsLogoutModalOpen(false);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || 'Gagal force logout pengguna.');
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'role') {
      const isNewRoleGlobal = value === 'owner' || value === 'admin';
      setFormData(prev => ({
        ...prev,
        role: value,
        cabang_id: isNewRoleGlobal ? '' : prev.cabang_id
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();

    if (isUsernameDuplicate) {
      toast.error('Username sudah digunakan! Silakan pilih username lain.');
      return;
    }

    if (isEmailDuplicate) {
      toast.error('Email sudah terdaftar! Silakan gunakan email lain.');
      return;
    }

    if (!formData.password || formData.password.length < 8) {
      toast.error('Password minimal 8 karakter!');
      return;
    }

    if (formData.password !== formData.password_confirmation) {
      toast.error('Konfirmasi password tidak cocok dengan password!');
      return;
    }

    if (!isGlobalRole && !formData.cabang_id) {
      toast.error('Silakan pilih cabang untuk role ini!');
      return;
    }

    try {
      setIsSubmitting(true);
      const payload = {
        ...formData,
        cabang_id: isGlobalRole ? null : (formData.cabang_id || null)
      };
      await http.post('/users', payload, {
        headers: { Authorization: getAuthorizationHeader() },
      });
      await fetchUsers();
      setIsAddModalOpen(false);
      setFormData({
        name: '', username: '', email: '', password: '', password_confirmation: '', role: 'kasir', cabang_id: ''
      });
      setShowPassword(false);
      setShowPasswordConfirm(false);
      toast.success('Berhasil membuat user baru!');
    } catch (err) {
      console.error(err);
      const responseData = err.response?.data;
      
      let errorMsg = 'Gagal membuat user baru, pastikan data yang dimasukkan benar.';
      
      if (responseData?.errors) {
        // Gabungkan semua pesan error validasi
        const validationErrors = Object.values(responseData.errors).flat().join(', ');
        errorMsg = `Data tidak valid: ${validationErrors}`;
      } else if (responseData?.message) {
        errorMsg = responseData.message;
      } else if (typeof responseData === 'string') {
        errorMsg = responseData;
      }
      
      toast.error(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <Layouts
    // title={'List Users'}
    >
      <div className="container mx-auto px-4 sm:px-8">
        <div className="py-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center -mx-4 sm:-mx-8 px-4 sm:px-8 mb-4 gap-4">
            <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100">Daftar Pengguna</h2>
            <div className="flex flex-col sm:flex-row gap-3 items-center w-full md:w-auto">
              <button 
                onClick={() => setIsAddModalOpen(true)}
                className="w-full sm:w-auto px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-md text-sm font-semibold shadow flex items-center justify-center gap-2"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Tambah Pengguna
              </button>
              <select
                value={filterRole}
                onChange={(e) => setFilterRole(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 dark:bg-slate-800 dark:border-slate-700 dark:text-white"
              >
                <option value="Semua">Semua Role</option>
                <option value="kasir">Kasir</option>
                <option value="kasir_pembelian">Kasir Pembelian</option>
                <option value="verifikator">Verifikator</option>
                <option value="operator">Operator</option>
                <option value="operator_rumah">Operator Rumah</option>
                <option value="kepala_toko">Kepala Toko</option>
                <option value="supervisor">Supervisor</option>
                <option value="admin">Admin</option>
                <option value="owner">Owner</option>
              </select>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 dark:bg-slate-800 dark:border-slate-700 dark:text-white"
              >
                <option value="Semua">Semua Status</option>
                <option value="Aktif">Aktif</option>
                <option value="Non-Aktif">Non-Aktif</option>
              </select>
            </div>
          </div>
          <div className="-mx-4 sm:-mx-8 px-4 sm:px-8 py-4 overflow-x-auto">
            <div className="inline-block min-w-full shadow-sm rounded-xl overflow-hidden border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
              {loading ? (
                <div className="flex flex-col items-center justify-center py-12 gap-3">
                  <div className="w-8 h-8 rounded-full animate-spin border-3 border-amber-200 dark:border-amber-800 border-t-amber-500"></div>
                  <span className="text-sm font-medium text-slate-500 dark:text-slate-400">Memuat data pengguna...</span>
                </div>
              ) : (
                <table className="min-w-full leading-normal text-left">
                  <thead>
                    <tr className="border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/80">
                      <th className="px-5 py-3.5 text-xs font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                        Nomor
                      </th>
                      <th className="px-5 py-3.5 text-xs font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                        Nama
                      </th>
                      <th className="px-5 py-3.5 text-xs font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                        Role
                      </th>
                      <th className="px-5 py-3.5 text-xs font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                        Status Akun
                      </th>
                      <th className="px-5 py-3.5 text-xs font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                        Sesi (Online)
                      </th>
                      <th className="px-5 py-3.5 text-xs font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                        Terakhir Login
                      </th>
                      <th className="px-5 py-3.5 text-xs font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                        Terakhir Logout
                      </th>
                      <th className="px-5 py-3.5 text-right text-xs font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                        Aksi
                      </th>
                      <th className="px-3 py-3.5" />
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800 bg-white dark:bg-slate-900">
                    {paginatedUsers.map((user, i) => (
                      <tr 
                        key={user.id} 
                        className="hover:bg-slate-50 dark:hover:bg-slate-800/60 bg-white dark:bg-slate-900 cursor-pointer transition-colors"
                        onClick={() => { setSelectedUser(user); setIsDetailModalOpen(true); }}
                      >
                        <td className="px-5 py-3 text-sm">
                          <p className="text-slate-500 dark:text-slate-400 font-medium whitespace-nowrap">{startIndex + i + 1}</p>
                        </td>
                        <td className="px-5 py-3 text-sm">
                          <p className="text-slate-900 dark:text-slate-100 font-semibold whitespace-nowrap">{user.name}</p>
                        </td>
                        <td className="px-5 py-3 text-sm whitespace-nowrap">
                          {getRoleBadge(user.role)}
                        </td>
                        <td className="px-5 py-3 text-sm whitespace-nowrap">
                          {user.is_active ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-800 dark:bg-green-950/60 dark:text-green-300 border border-green-200 dark:border-green-800">
                              Aktif
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300 border border-rose-200 dark:border-rose-800">
                              Non-Aktif
                            </span>
                          )}
                        </td>
                        <td className="px-5 py-3 text-sm whitespace-nowrap">
                          {user.is_login ? (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800">
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                              Online
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400 border border-slate-200 dark:border-slate-700">
                              Offline
                            </span>
                          )}
                        </td>
                        <td className="px-5 py-3 text-sm whitespace-nowrap">
                          <p className="text-slate-600 dark:text-slate-400">
                            {user.last_login ? new Date(user.last_login).toLocaleString('id-ID', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
                          </p>
                        </td>
                        <td className="px-5 py-3 text-sm whitespace-nowrap">
                          <p className="text-slate-600 dark:text-slate-400">
                            {user.last_logout ? new Date(user.last_logout).toLocaleString('id-ID', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
                          </p>
                        </td>
                        <td className="px-5 py-3 text-sm text-right space-x-2 whitespace-nowrap">
                          <button 
                            onClick={(e) => { e.stopPropagation(); setSelectedUser(user); setIsModalOpen(true); }}
                            type="button" 
                            title={user.is_active ? "Non-Aktifkan User" : "Aktifkan User"}
                            className={`inline-block px-3 py-1 rounded-md text-white text-xs font-bold transition-colors shadow-sm ${user.is_active ? 'bg-orange-500 hover:bg-orange-600' : 'bg-green-600 hover:bg-green-700'}`}
                          >
                            {user.is_active ? "Non-Aktifkan" : "Aktifkan"}
                          </button>

                          <button onClick={(e) => e.stopPropagation()} type="button" className="inline-block text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 ml-2 transition-colors">
                            <svg className="inline-block h-5 w-5 fill-current" viewBox="0 0 24 24">
                              <path d="M12 6a2 2 0 110-4 2 2 0 010 4zm0 8a2 2 0 110-4 2 2 0 010 4zm-2 6a2 2 0 104 0 2 2 0 00-4 0z" />
                            </svg>
                          </button>
                        </td>
                        <td className="px-2 py-3" />
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            
            {/* Pagination Controls */}
            {!loading && filteredUsers.length > 0 && (
              <div className="flex flex-col sm:flex-row items-center justify-between bg-white dark:bg-slate-900 px-4 py-3 sm:px-6 mt-4 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800">
                <div className="flex items-center text-sm text-slate-600 dark:text-slate-400">
                  Menampilkan {startIndex + 1} sampai {Math.min(startIndex + itemsPerPage, filteredUsers.length)} dari {filteredUsers.length} entri
                </div>
                <div className="flex items-center space-x-2 mt-4 sm:mt-0">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium border transition-colors ${currentPage === 1 ? 'bg-slate-100 dark:bg-slate-800 text-slate-400 dark:text-slate-600 border-slate-200 dark:border-slate-700 cursor-not-allowed' : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 border-slate-300 dark:border-slate-700'}`}
                  >
                    Sebelumnya
                  </button>
                  <span className="text-sm text-slate-700 dark:text-slate-300 font-medium">
                    Halaman {currentPage} dari {lastPage === 0 ? 1 : lastPage}
                  </span>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, lastPage))}
                    disabled={currentPage === lastPage || lastPage === 0}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium border transition-colors ${currentPage === lastPage || lastPage === 0 ? 'bg-slate-100 dark:bg-slate-800 text-slate-400 dark:text-slate-600 border-slate-200 dark:border-slate-700 cursor-not-allowed' : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 border-slate-300 dark:border-slate-700'}`}
                  >
                    Selanjutnya
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Detail Modal */}
      {isDetailModalOpen && selectedUser && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm transition-opacity" aria-hidden="true" onClick={() => setIsDetailModalOpen(false)}></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white dark:bg-slate-900 rounded-xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full border border-slate-200 dark:border-slate-800">
              <div className="px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4 border-b border-slate-200 dark:border-slate-800 pb-2">Detail Pengguna</h3>
                <div className="space-y-3 text-sm text-gray-700 dark:text-slate-300">
                  <p><span className="font-semibold text-gray-900 dark:text-slate-100 w-32 inline-block">Nama:</span> {selectedUser.name}</p>
                  <p><span className="font-semibold text-gray-900 dark:text-slate-100 w-32 inline-block">Email:</span> {selectedUser.email}</p>
                  <p><span className="font-semibold text-gray-900 dark:text-slate-100 w-32 inline-block">Role:</span> <span className="capitalize">{selectedUser.role}</span></p>
                  <p><span className="font-semibold text-gray-900 dark:text-slate-100 w-32 inline-block">Status Akun:</span> {selectedUser.is_active ? <span className="text-green-600 font-bold">Aktif</span> : <span className="text-red-600 font-bold">Non-Aktif</span>}</p>
                  <p><span className="font-semibold text-gray-900 dark:text-slate-100 w-32 inline-block">Sesi (Online):</span> {selectedUser.is_login ? <span className="text-emerald-600 font-bold">Online</span> : <span className="text-gray-400 font-bold">Offline</span>}</p>
                  <p>
                    <span className="font-semibold text-gray-900 dark:text-slate-100 w-32 inline-block">Cabang:</span>{' '}
                    {(Array.isArray(selectedUser.role) ? selectedUser.role.some(r => r === 'owner' || r === 'admin' || r === 'supervisor') : (selectedUser.role === 'owner' || selectedUser.role === 'admin' || selectedUser.role === 'supervisor')) ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-amber-100 dark:bg-amber-950/60 text-amber-800 dark:text-amber-300 border border-amber-200 dark:border-amber-800">
                        Semua Cabang (Akses Global)
                      </span>
                    ) : selectedUser.cabang ? (
                      selectedUser.cabang.nama_cabang
                    ) : (
                      '-'
                    )}
                  </p>
                  <p><span className="font-semibold text-gray-900 dark:text-slate-100 w-32 inline-block">Terakhir Login:</span> {selectedUser.last_login ? new Date(selectedUser.last_login).toLocaleString('id-ID', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '-'}</p>
                  <p><span className="font-semibold text-gray-900 dark:text-slate-100 w-32 inline-block">Terakhir Logout:</span> {selectedUser.last_logout ? new Date(selectedUser.last_logout).toLocaleString('id-ID', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '-'}</p>
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-slate-800/80 px-4 py-3 sm:px-6 flex justify-end border-t border-slate-200 dark:border-slate-800">
                <button type="button" className="rounded-md border border-gray-300 dark:border-slate-700 shadow-sm px-4 py-2 bg-white dark:bg-slate-800 text-sm font-medium text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors" onClick={() => setIsDetailModalOpen(false)}>Tutup</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {isModalOpen && selectedUser && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm transition-opacity" aria-hidden="true" onClick={() => setIsModalOpen(false)}></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white dark:bg-slate-900 rounded-xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-md sm:w-full border border-slate-200 dark:border-slate-800">
              <div className="px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className={`mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full sm:mx-0 sm:h-10 sm:w-10 ${selectedUser.is_active ? 'bg-red-100 dark:bg-red-950/60' : 'bg-green-100 dark:bg-green-950/60'}`}>
                    <svg className={`h-6 w-6 ${selectedUser.is_active ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100" id="modal-title">
                      Konfirmasi Perubahan Status
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500 dark:text-slate-400">
                        Apakah Anda yakin ingin <strong>{selectedUser.is_active ? 'menonaktifkan' : 'mengaktifkan'}</strong> pengguna dengan nama <strong className="text-slate-900 dark:text-white">{selectedUser.name}</strong>?
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-slate-800/80 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse border-t border-slate-200 dark:border-slate-800">
                <button
                  type="button"
                  className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm transition-colors ${selectedUser.is_active ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500' : 'bg-green-600 hover:bg-green-700 focus:ring-green-500'}`}
                  onClick={() => { toggleStatus(selectedUser.id); setIsModalOpen(false); }}
                >
                  Ya, {selectedUser.is_active ? 'Non-Aktifkan' : 'Aktifkan'}
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-slate-700 shadow-sm px-4 py-2 bg-white dark:bg-slate-800 text-base font-medium text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm transition-colors"
                  onClick={() => setIsModalOpen(false)}
                >
                  Batal
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Force Logout Confirmation Modal */}
      {isLogoutModalOpen && selectedUser && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-900/60 backdrop-blur-sm" aria-hidden="true" onClick={() => setIsLogoutModalOpen(false)}></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block px-4 pt-5 pb-4 overflow-hidden text-left align-bottom transition-all transform bg-white dark:bg-slate-900 rounded-xl shadow-xl sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6 border border-slate-200 dark:border-slate-800">
              <div className="sm:flex sm:items-start">
                <div className="flex items-center justify-center flex-shrink-0 w-12 h-12 mx-auto bg-red-100 dark:bg-red-950/60 rounded-full sm:mx-0 sm:h-10 sm:w-10">
                  <svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-gray-100" id="modal-title">
                    Force Logout Pengguna
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500 dark:text-slate-400">
                      Yakin ingin memaksa keluar (force logout) <strong className="text-slate-900 dark:text-white">{selectedUser.name}</strong>? Pengguna akan segera dikeluarkan dari sistem dan token aksesnya akan hangus.
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                <button 
                  type="button" 
                  className="inline-flex justify-center w-full px-4 py-2 text-base font-medium text-white bg-red-600 border border-transparent rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm transition-colors"
                  onClick={handleForceLogout}
                  disabled={loading}
                >
                  {loading ? 'Memproses...' : 'Ya, Logout Sekarang'}
                </button>
                <button 
                  type="button" 
                  className="inline-flex justify-center w-full px-4 py-2 mt-3 text-base font-medium text-gray-700 dark:text-slate-200 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-700 rounded-md shadow-sm hover:bg-gray-50 dark:hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 sm:mt-0 sm:w-auto sm:text-sm transition-colors"
                  onClick={() => setIsLogoutModalOpen(false)}
                >
                  Batal
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add User Modal */}
      {isAddModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="add-modal-title" role="dialog" aria-modal="true">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm transition-opacity" aria-hidden="true" onClick={() => setIsAddModalOpen(false)}></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white dark:bg-slate-900 rounded-xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg w-full border border-slate-200 dark:border-slate-800">
              <form onSubmit={handleAddUser}>
                <div className="px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                      <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100 mb-4 border-b border-slate-200 dark:border-slate-800 pb-2" id="add-modal-title">
                        Tambah Pengguna Baru
                      </h3>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-slate-300">Nama Lengkap</label>
                          <input type="text" name="name" required value={formData.name} onChange={handleInputChange} placeholder="Nama lengkap user" className="mt-1 block w-full border border-gray-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white rounded-md shadow-sm focus:ring-amber-500 focus:border-amber-500 sm:text-sm px-3 py-2" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-slate-300">Username</label>
                          <input 
                            type="text" 
                            name="username" 
                            required 
                            minLength={5} 
                            value={formData.username} 
                            onChange={handleInputChange} 
                            placeholder="Minimal 5 karakter" 
                            className={`mt-1 block w-full border ${isUsernameDuplicate ? 'border-rose-500 focus:ring-rose-500 focus:border-rose-500' : 'border-gray-300 dark:border-slate-700 focus:ring-amber-500 focus:border-amber-500'} bg-white dark:bg-slate-800 text-gray-900 dark:text-white rounded-md shadow-sm sm:text-sm px-3 py-2`} 
                          />
                          {isUsernameDuplicate && (
                            <p className="text-xs text-rose-500 mt-1 font-medium">
                              ⚠️ Username sudah digunakan! Silakan gunakan username lain.
                            </p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-slate-300">Email</label>
                          <input 
                            type="email" 
                            name="email" 
                            required 
                            value={formData.email} 
                            onChange={handleInputChange} 
                            placeholder="contoh@mail.com" 
                            className={`mt-1 block w-full border ${isEmailDuplicate ? 'border-rose-500 focus:ring-rose-500 focus:border-rose-500' : 'border-gray-300 dark:border-slate-700 focus:ring-amber-500 focus:border-amber-500'} bg-white dark:bg-slate-800 text-gray-900 dark:text-white rounded-md shadow-sm sm:text-sm px-3 py-2`} 
                          />
                          {isEmailDuplicate && (
                            <p className="text-xs text-rose-500 mt-1 font-medium">
                              ⚠️ Email sudah terdaftar! Silakan gunakan email lain.
                            </p>
                          )}
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300">Password</label>
                            <div className="relative mt-1">
                              <input 
                                type={showPassword ? 'text' : 'password'} 
                                name="password" 
                                required 
                                minLength={8} 
                                value={formData.password} 
                                onChange={handleInputChange} 
                                placeholder="Minimal 8 karakter"
                                className="block w-full pr-10 border border-gray-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white rounded-md shadow-sm focus:ring-amber-500 focus:border-amber-500 sm:text-sm px-3 py-2" 
                              />
                              <button
                                type="button"
                                tabIndex={-1}
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 cursor-pointer"
                              >
                                {showPassword ? <IconEye size={18} /> : <IconEyeOff size={18} />}
                              </button>
                            </div>
                            {formData.password && formData.password.length < 8 && (
                              <p className="text-[11px] text-amber-500 mt-1">Minimal 8 karakter</p>
                            )}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300">Konfirmasi Password</label>
                            <div className="relative mt-1">
                              <input 
                                type={showPasswordConfirm ? 'text' : 'password'} 
                                name="password_confirmation" 
                                required 
                                minLength={8} 
                                value={formData.password_confirmation} 
                                onChange={handleInputChange} 
                                placeholder="Ulangi password"
                                className={`block w-full pr-10 border ${isPasswordMismatch ? 'border-rose-500 focus:ring-rose-500 focus:border-rose-500' : 'border-gray-300 dark:border-slate-700 focus:ring-amber-500 focus:border-amber-500'} bg-white dark:bg-slate-800 text-gray-900 dark:text-white rounded-md shadow-sm sm:text-sm px-3 py-2`} 
                              />
                              <button
                                type="button"
                                tabIndex={-1}
                                onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}
                                className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 cursor-pointer"
                              >
                                {showPasswordConfirm ? <IconEye size={18} /> : <IconEyeOff size={18} />}
                              </button>
                            </div>
                            {isPasswordMismatch && (
                              <p className="text-xs text-rose-500 mt-1 font-medium">
                                ⚠️ Konfirmasi password tidak cocok!
                              </p>
                            )}
                            {!isPasswordMismatch && formData.password && formData.password === formData.password_confirmation && (
                              <p className="text-xs text-emerald-500 mt-1 font-medium">
                                ✓ Password cocok
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300">Role</label>
                            <select name="role" required value={formData.role} onChange={handleInputChange} className="mt-1 block w-full border border-gray-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white rounded-md shadow-sm focus:ring-amber-500 focus:border-amber-500 sm:text-sm px-3 py-2">
                              <option value="kasir">Kasir</option>
                              <option value="kasir_pembelian">Kasir Pembelian</option>
                              <option value="verifikator">Verifikator</option>
                              <option value="operator">Operator</option>
                              <option value="operator_rumah">Operator Rumah</option>
                              <option value="kepala_toko">Kepala Toko</option>
                              <option value="supervisor">Supervisor</option>
                              <option value="admin">Admin</option>
                              <option value="owner">Owner</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300">
                              Pilih Cabang {isGlobalRole && <span className="text-xs font-normal text-amber-600 dark:text-amber-400">(Akses Global)</span>}
                            </label>
                            {isGlobalRole ? (
                              <div className="mt-1 flex items-center justify-between px-3 py-2 border border-dashed border-amber-300 dark:border-amber-700/60 bg-amber-50/70 dark:bg-amber-950/30 rounded-md text-amber-800 dark:text-amber-300 text-xs sm:text-sm">
                                <span className="font-bold flex items-center gap-1.5">
                                  <span>🌐</span> Semua Cabang
                                </span>
                                <span className="text-[11px] text-amber-600 dark:text-amber-400 font-medium">
                                  Akses Seluruh Toko
                                </span>
                              </div>
                            ) : (
                              <select 
                                name="cabang_id" 
                                required 
                                value={formData.cabang_id} 
                                onChange={handleInputChange} 
                                className="mt-1 block w-full border border-gray-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white rounded-md shadow-sm focus:ring-amber-500 focus:border-amber-500 sm:text-sm px-3 py-2 cursor-pointer"
                              >
                                <option value="">-- Pilih Cabang --</option>
                                {Array.isArray(cabangList) && cabangList
                                  .filter(cabang => cabang.nama_cabang && !cabang.nama_cabang.toLowerCase().includes('brankas'))
                                  .map(cabang => (
                                  <option key={cabang.id} value={cabang.id}>{cabang.nama_cabang}</option>
                                ))}
                              </select>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 dark:bg-slate-800/80 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse border-t border-slate-200 dark:border-slate-800">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm transition-colors ${isSubmitting ? 'bg-amber-400 cursor-not-allowed' : 'bg-amber-600 hover:bg-amber-700 focus:ring-amber-500'}`}
                  >
                    {isSubmitting ? 'Menyimpan...' : 'Simpan'}
                  </button>
                  <button
                    type="button"
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-slate-700 shadow-sm px-4 py-2 bg-white dark:bg-slate-800 text-base font-medium text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm transition-colors"
                    onClick={() => setIsAddModalOpen(false)}
                  >
                    Batal
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </Layouts>
  );
}
